import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import ImageCarousel from './ImageCarousel';
import { useTranslation } from '../contexts/TranslationContext';
import { getLocalizedString, type LocalizedString } from '../utils/localizedText';

export type PageHeroCarouselSectionConfig = {
  enabled?: boolean;
  title?: LocalizedString;
  subtitle?: LocalizedString;
  ctaText?: LocalizedString;
  ctaUrl?: string;
  align?: 'left' | 'right';
  minHeight?: number;
  images?: string[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showArrows?: boolean;
  showDots?: boolean;
};

type Props = {
  config?: PageHeroCarouselSectionConfig | null;
  resolvePublicUrl?: (p?: string) => string;
};

const clamp = (n: any, min: number, max: number) => Math.max(min, Math.min(max, Number(n) || 0));

const PageHeroCarouselSection: React.FC<Props> = ({ config, resolvePublicUrl }) => {
  const cfg: PageHeroCarouselSectionConfig = (config && typeof config === 'object') ? config : {};

  const { language } = useTranslation();

  const title = getLocalizedString(cfg.title, language, 'en', 'Echt luxuriöse Uhren');
  const subtitle = getLocalizedString(cfg.subtitle, language, 'en', 'Entdecke neue Lieblinge mit geprüfter Qualität.');
  const ctaText = getLocalizedString(cfg.ctaText, language, 'en', '');
  const ctaUrl = typeof cfg.ctaUrl === 'string' ? cfg.ctaUrl : '';
  const align: 'left' | 'right' = cfg.align === 'right' ? 'right' : 'left';
  const minHeight = clamp(cfg.minHeight, 360, 900) || 520;
  const autoPlay = cfg.autoPlay !== false;
  const autoPlayInterval = clamp(cfg.autoPlayInterval, 1500, 20000) || 4500;
  const showArrows = cfg.showArrows !== false;
  const showDots = cfg.showDots !== false;

  const resolve = React.useCallback((p?: string) => {
    if (!p) return '';
    return resolvePublicUrl ? resolvePublicUrl(p) : p;
  }, [resolvePublicUrl]);

  const images = Array.isArray(cfg.images)
    ? cfg.images.filter((s: any) => typeof s === 'string' && s).map((s: string) => resolve(s))
    : [];

  if (cfg.enabled === false) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight,
        bgcolor: 'grey.900',
        overflow: 'hidden',
      }}
    >
      {/* Background carousel */}
      {images.length > 0 ? (
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <ImageCarousel
            images={images}
            autoPlay={autoPlay}
            autoPlayInterval={autoPlayInterval}
            showControls={showArrows}
            showDots={showDots}
            height={minHeight}
            borderRadius={0}
            boxShadow={0}
          />
        </Box>
      ) : (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'grey.900' }} />
      )}

      {/* Overlay gradient for contrast */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            align === 'left'
              ? 'linear-gradient(90deg, rgba(2,6,23,0.82) 0%, rgba(2,6,23,0.55) 38%, rgba(2,6,23,0.10) 78%, rgba(2,6,23,0.00) 100%)'
              : 'linear-gradient(270deg, rgba(2,6,23,0.82) 0%, rgba(2,6,23,0.55) 38%, rgba(2,6,23,0.10) 78%, rgba(2,6,23,0.00) 100%)',
        }}
      />

      <Container maxWidth={false} sx={{ position: 'relative', py: { xs: 7, md: 10 } }}>
        <Box
          sx={{
            maxWidth: 1240,
            mx: 'auto',
            display: 'flex',
            justifyContent: align === 'left' ? 'flex-start' : 'flex-end',
          }}
        >
          <Box
            sx={{
              maxWidth: 560,
              color: 'common.white',
              textAlign: 'left',
              px: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 950,
                lineHeight: 1.05,
                letterSpacing: -0.6,
                textShadow: '0 10px 34px rgba(0,0,0,0.45)',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mt: 2,
                color: 'rgba(255,255,255,0.92)',
                textShadow: '0 10px 34px rgba(0,0,0,0.45)',
                maxWidth: 520,
              }}
            >
              {subtitle}
            </Typography>

            {ctaText && ctaUrl ? (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  href={ctaUrl}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.25,
                    fontWeight: 900,
                    textTransform: 'none',
                    boxShadow: '0 16px 50px rgba(59,130,246,0.35)',
                  }}
                >
                  {ctaText}
                </Button>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PageHeroCarouselSection;
